import {NumberUtils} from "../../utils/number-utils";
import {service, RepositoryContextService} from '@ontotext/workbench-api';

import {select} from 'd3';

const d3 = {
  select,
};

const STATUS = {
    'WAIT': 'WAIT',
    'NO_REPO': 'NO_REPO',
    'READY': 'READY',
    'IN_PROGRESS': 'IN_PROGRESS',
    'ERROR': 'ERROR',
};

const modules = [
    'ui.scroll.jqlite',
    'ui.scroll',
    'toastr',
    'ui.bootstrap',
    'graphdb.framework.core.services.repositories',
    'graphdb.framework.utils.localstorageadapter',
];
const allGraphs = {
    contextID: {
        type: "all",
        value: "all.graphs.label",
        uri: "",
    },
};
Object.defineProperty(global, 'allGraphs', {
    get: () => {
        return allGraphs;
    },
});

angular
    .module('graphdb.framework.graphexplore.controllers.dependencies', modules)
    .controller('DependenciesChordCtrl', DependenciesChordCtrl)
    .config(['$uibTooltipProvider', function($uibTooltipProvider) {
        $uibTooltipProvider.options({appendToBody: true});
    }])
    .filter('humanize', function() {
        return humanize;
    });

function humanize(number) {
    if (number < 1000) {
        return number;
    }
    const si = ['K', 'M', 'G', 'T', 'P', 'H'];
    const exp = Math.floor(Math.log(number) / Math.log(1000));
    let result = number / Math.pow(1000, exp);
    result = (result % 1 > (1 / Math.pow(1000, exp - 1))) ? result.toFixed(2) : result.toFixed(0);
    return result + si[exp - 1];
}

DependenciesChordCtrl.$inject = ['$scope', '$rootScope', '$repositories', 'toastr', '$timeout', '$interval', 'GraphDataRestService', 'UiScrollService', 'ModalService', 'LocalStorageAdapter', 'RDF4JRepositoriesRestService', '$translate'];

function DependenciesChordCtrl($scope, $rootScope, $repositories, toastr, $timeout, $interval, GraphDataRestService, UiScrollService, ModalService, LocalStorageAdapter, RDF4JRepositoriesRestService, $translate) {
    const repositoryContextService = service(RepositoryContextService);

    let timer = null;

    /**
     * Defines the maximum number of graphs to be loaded in the view.
     *
     * The view becomes too heavy when displaying all graphs, which can impact performance.
     * This constant limits the number of graphs that will be loaded to ensure smooth rendering.
     */
    const MAX_LOADED_GRAPHS = 1000;
    $scope.hasMoreGraphs = false;

    $scope.status = repositoryContextService.getSelectedRepository()?.id ? STATUS.WAIT : STATUS.NO_REPO;

    $scope.graphsDropdownToggled = (isOpen) => {
        if ($scope.hasMoreGraphs && isOpen) {
            toastr.warning($translate.instant('dependencies.graphs.too.many.warning', {graphsLimit: NumberUtils.formatNumberToLocaleString(MAX_LOADED_GRAPHS, $translate.use())}));
        }
    };

    //allGraphs is used to include all graphs in the chosen repository and represent the Class Relationships diagram and table,
    // while graphsInRepo is sliced to 1000 if there are more than 1000 graphs in the repository, and they are present
    // in the drop-down menu otherwise browsers crash.
    let selectedGraph = allGraphs;

    const initView = function() {
        // Try to load one more file than the maximum limit.
        // This allows us to check if there is at least one additional file beyond the set max limit.
        RDF4JRepositoriesRestService.resolveGraphs(repositoryContextService.getSelectedRepository()?.id, MAX_LOADED_GRAPHS + 1)
            .success(function(graphsInRepo) {
                $scope.graphsInRepo = graphsInRepo.results.bindings;
                // Determines if there are more files than the specified limit.
                // We increase the limit in the check because:
                // - One extra file is added before the request is sent.
                // - Another file (the default graph) is added by the service when the response is received.
                $scope.hasMoreGraphs = $scope.graphsInRepo.length > MAX_LOADED_GRAPHS + 2;
                setSelectedGraphFromCache();
                if (!$scope.isSystemRepository()) {
                    $scope.status = 'WAIT';
                    getRelationshipsStatus(true);
                } else if ($scope.status !== "READY") {
                    getRelationshipsStatus();
                }
            }).error(function(data) {
            $scope.repositoryError = getError(data);
            toastr.error(getError(data), $translate.instant('graphexplore.error.getting.graph'));
        });
    };

    const setSelectedGraphFromCache = function() {
        const selGraphFromCache = LocalStorageAdapter.get(`dependencies-selectedGraph-${repositoryContextService.getSelectedRepository()?.id}`);
        if (selGraphFromCache !== null && $scope.graphsInRepo.some((graph) => graph.contextID.uri === selGraphFromCache.contextID.uri)) {
            selectedGraph = selGraphFromCache;
        } else {
            LocalStorageAdapter.set(`dependencies-selectedGraph-${repositoryContextService.getSelectedRepository()?.id}`, selectedGraph);
        }
    };

    const getRelationshipsData = function(selectedClasses) {
        d3.select('#dependencies-chord').html('');

        $scope.status = STATUS.WAIT;

        GraphDataRestService.getRelationshipsData(selectedClasses, $scope.direction, selectedGraph.contextID.uri)
            .success(function(matrixData) {
                // Check classes empty
                $scope.dependenciesData = {
                    matrix: matrixData.right,
                    nodes: matrixData.left,
                    hasLinks: _.sum(_.map(matrixData.right, function(arr) {
                        return _.sum(arr);
                    })) > 0,
                    direction: $scope.direction,
                };
                $scope.status = STATUS.READY;
            }).error(function(data) {
            $scope.status = STATUS.READY;
            toastr.error(getError(data), $translate.instant('graphexplore.error.dependencies.count'));
        });
    };

    const getRelationshipsClasses = function() {
        GraphDataRestService.getRelationshipsClasses($scope.direction, selectedGraph.contextID.uri)
            .success(function(classesData, status) {
                $scope.allClasses.items = _.filter(classesData, classFilterFunc);
                $scope.allNotFilteredClasses = classesData;
                $scope.selectedClasses = undefined;
                if (angular.isUndefined($scope.selectedClasses)) {
                    $scope.selectedClasses = classesData.slice(0, 10);
                }
                getRelationshipsData($scope.selectedClasses);
                if (status === 207) {
                    toastr.warning($translate.instant('graphexplore.update.diagram'), $translate.instant('graphexplore.repository.data.changed'));
                }
            });
    };

    const getRelationshipsStatus = function(force) {
        if ($scope.status === STATUS.READY && !force) {
            return;
        }
        $scope.status = STATUS.WAIT;
        GraphDataRestService.getRelationshipsStatus(selectedGraph.contextID.uri)
            .success(function(data) {
                $scope.status = data;
                if ($scope.status === STATUS.IN_PROGRESS) {
                    if (timer !== null) {
                        return;
                    } else {
                        timer = $interval(() => getRelationshipsStatus(), 2000);
                    }
                }
                if ($scope.status === STATUS.READY) {
                    if (timer !== null) {
                        $interval.cancel(timer);
                        timer = null;
                    }
                    getRelationshipsClasses();
                }
                if ($scope.status.indexOf('ERROR;') === 0) {
                    $scope.status = STATUS.ERROR;
                    if (timer !== null) {
                        $interval.cancel(timer);
                        timer = null;
                    }
                    toastr.error($translate.instant('graphexplore.error.dependencies.calc', {error:$scope.status.substring('ERROR;'.length)}));
                }
            })
            .error(function(data) {
                $scope.status = STATUS.ERROR;
                toastr.error(getError(data), $translate.instant('graphexplore.error.dependencies.count.status'));
            });
    };

    $scope.allClasses = {};
    $scope.allClasses.items = [];
    $scope.allNotFilteredClasses = [];
    $scope.direction = 'all';

    const datasource = {};
    let position = 0;
    let current = 0;
    $rootScope.key = '';

    datasource.get = function(index, count, success) {
        UiScrollService.initLazyList(index, count, success, position, $scope.allClasses.items);
    };

    $rootScope.$watch(function() {
        return $rootScope.key;
    }, function() {
        position = 0;
        _.each($scope.allClasses.items, function(item) {
            if ($rootScope.key > item) position++;
        });
        current++;
    });

    datasource.revision = function() {
        return current;
    };

    $scope.datasource = datasource;

    // adapter implementation for ui-scroll directive
    // reload
    $scope.adapterContainer = {adapter: {remain: true}};

    $scope.classQuery = {};
    $scope.classQuery.query = '';
    $scope.classFilterFunc = classFilterFunc;

    function classFilterFunc(c) {
        return c.name
            .toLowerCase()
            .indexOf($scope.classQuery.query.toLowerCase()) !== -1;
    }

    $scope.$watch('allClasses.items', function() {
        if ($scope.allClasses.items.length > 0) {
            $timeout(function() {
                $scope.adapterContainer.adapter.reload();
            }, 30);
        }
    });

    $scope.$watch('direction', function() {
        const activeRepository = repositoryContextService.getSelectedRepository();
        if (!activeRepository || $scope.isSystemRepository() || activeRepository.isFedx()) {
            return;
        }
        initView();
    });

    $scope.$on('$destroy', function() {
        $interval.cancel(timer);
        repositoryChangedSubscription();
    });

    $scope.isLoading = function() {
        return $scope.status === STATUS.IN_PROGRESS || $scope.status === STATUS.WAIT;
    };

    $scope.confirmCalculateDependencies = function() {
        ModalService.openSimpleModal({
            title: $translate.instant('confirm.operation'),
            message: $translate.instant('graphexplore.calculating.relationships'),
            warning: true,
        }).result
            .then(function() {
                $scope.calculateDependencies();
            });
    };

    $scope.calculateDependencies = function() {
        $scope.status = STATUS.WAIT;
        $scope.selectedClasses = undefined;
        GraphDataRestService.calculateRelationships(selectedGraph.contextID.uri)
            .success(function(data) {
                if (data.indexOf('ERROR;') === 0) {
                    toastr.error($translate.instant('graphexplore.error.dependencies.calc', {error:$scope.status.substring('ERROR;'.length)}));
                } else {
                    getRelationshipsStatus();
                }
            })
            .error(function(data) {
                toastr.error($translate.instant('graphexplore.error.could.not.force.count', {error: getError(data)}));
            });
    };

    $scope.addClass = function(clazz) {
        $scope.selectedClasses.push(clazz);
        getRelationshipsData($scope.selectedClasses);
    };

    $scope.removeClass = function(clazz) {
        _.remove($scope.selectedClasses, function(c) {
            return c.name === clazz.name;
        });
        getRelationshipsData($scope.selectedClasses);
    };

    $scope.showClass = function(clazz) {
        $scope.classToShow = clazz;
    };

    $scope.isClassByNameShown = function(name) {
        return _.find($scope.selectedClasses, {name: name}) !== undefined;
    };

    $scope.addClassByName = function(name) {
        $scope.selectedClasses.push(_.find($scope.allClasses.items, {name: name}));
        getRelationshipsData($scope.selectedClasses);
    };

    $scope.removeClassByName = function(name) {
        _.remove($scope.selectedClasses, function(c) {
            return c.name === name;
        });
        getRelationshipsData($scope.selectedClasses);
    };


    $scope.removeAllClasses = function() {
        $scope.selectedClasses = [];
        getRelationshipsData($scope.selectedClasses);
    };

    $scope.isSystemRepository = function() {
        return repositoryContextService.getSelectedRepository()?.id === 'SYSTEM';
    };

    function onRepositoryIsSet() {
        // clear class search on changing the repository
        $scope.classQuery = {};
        $scope.classQuery.query = '';
        $scope.repositoryError = null;
        selectedGraph = allGraphs;
        const activeRepository = repositoryContextService.getSelectedRepository();
        if (!activeRepository || activeRepository.isFedx()) {
            $scope.status = STATUS.NO_REPO;
            return;
        }
        initView();
    }


    const repositoryChangedSubscription = repositoryContextService.onSelectedRepositoryChanged(() => onRepositoryIsSet());

    $scope.selectGraph = function(graph) {
        selectedGraph = graph;
        getRelationshipsStatus(true);
        LocalStorageAdapter.set(`dependencies-selectedGraph-${repositoryContextService.getSelectedRepository()?.id}`, selectedGraph);
    };

    $scope.getSelectedGraphValue = function() {
        return selectedGraph.contextID.value;
    };

    $scope.isAllGraphsSelected = function() {
        return $scope.getSelectedGraphValue() === 'all.graphs.label';
    };
}
